import { useState, useEffect } from 'react';

import { TextField } from 'react-admin';

import { flowchartLogger } from 'src/lib/logger';

import ResourceList from '../../components/ResourceList';
import NodeControlForm from '../NodeControlForm';

import type { NodeControlsProps } from '../../dataProvider/types';
import type { DistributionListRead } from '@ragu-components/ord-types';

export interface DistributionListType extends Record<string, unknown> {
  distribution_list: DistributionListRead | null;
}

interface DistributionListControlsProps extends NodeControlsProps {
  updateNodeData: (data: Record<string, unknown>) => void;
  data: DistributionListType;
}

export default function DistributionListControls({
  updateNodeData,
  data,
}: DistributionListControlsProps): React.ReactElement {
  const [distributionList, setDistributionList] = useState<DistributionListRead | null>(data.distribution_list ?? null);

  const onDistributionListDataUpdate = (event: React.MouseEvent | React.FormEvent): void => {
    event.preventDefault();
    flowchartLogger.debug('DistributionListControls: onDistributionListDataUpdate', {
      distributionlist: distributionList,
    });
    if (distributionList === undefined || distributionList === null) {
      flowchartLogger.debug('DistributionListControls: distributionlist is undefined or empty. Update cancelled.');
      return;
    }
    updateNodeData({ distribution_list: distributionList });
  };

  useEffect(() => {
    flowchartLogger.debug('DistributionListControls: autosave', { distributionlist: distributionList });
    updateNodeData({ distribution_list: distributionList });
  }, [distributionList, updateNodeData]);

  return (
    <NodeControlForm
      onSubmit={onDistributionListDataUpdate}
      isDisabled={distributionList === undefined || distributionList === null}
      data={data}
      updateNodeData={updateNodeData}
    >
      <ResourceList
        resource='distribution_lists'
        selectedResourceId={distributionList !== null ? distributionList.id : null}
        onRowClick={(_id, _resource, record) => {
          flowchartLogger.debug('DistributionListControls: onRowClick', { record });
          setDistributionList(record as DistributionListRead);
          return false;
        }}
      >
        <TextField source={'name'} />
      </ResourceList>
    </NodeControlForm>
  );
}
